import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { styleFor, summariseTooth } from "@/lib/dental";
import { surfaceSides } from "./ToothSvg";
import { Trash2 } from "lucide-react";

// The primary charting surface.
//
// On the grid a single surface region is only ~18px across — far under the 44px
// touch guidance, and dentists chart wearing gloves. So tapping a tooth opens it
// here at a size that can actually be hit, rather than asking anyone to land on
// a 20px triangle.
const REGION_PATHS = {
  top:    "M0,0 L200,0 L140,60 L60,60 Z",
  bottom: "M0,200 L200,200 L140,140 L60,140 Z",
  left:   "M0,0 L60,60 L60,140 L0,200 Z",
  right:  "M200,0 L140,60 L140,140 L200,200 Z",
};

export default function ToothDetailSheet({
  open,
  onOpenChange,
  tooth,
  entry,
  meta,
  onApply,
}) {
  const [selected, setSelected] = useState([]);
  const [findingType, setFindingType] = useState("caries");
  const [status, setStatus] = useState("existing");
  const [material, setMaterial] = useState("");
  const [note, setNote] = useState("");

  if (!tooth) return null;

  const sides = surfaceSides(tooth);
  const allowed = meta?.surfaces_by_tooth?.[tooth] || [];
  const superseding = meta?.superseding_findings || [];
  const isWholeToothFinding = superseding.includes(findingType) ||
    ["root_canal", "crown", "implant", "bridge_pontic", "bridge_abutment", "mobility", "watch"].includes(findingType);

  const toggle = (letter) => {
    if (!allowed.includes(letter)) return;
    setSelected((prev) => prev.includes(letter) ? prev.filter((x) => x !== letter) : [...prev, letter]);
  };

  const reset = () => {
    setSelected([]); setNote(""); setMaterial("");
  };

  const apply = () => {
    onApply({
      tooth,
      surfaces: isWholeToothFinding ? [] : selected,
      finding_type: findingType,
      status,
      material,
      note,
    });
    reset();
    onOpenChange(false);
  };

  const region = (key, d) => {
    const letter = sides[key];
    const usable = allowed.includes(letter);
    const isSel = selected.includes(letter);
    const existing = entry?.surfaces?.[letter];
    const st = existing ? styleFor(existing.finding_type) : null;
    return (
      <g key={key}>
        <path
          d={d}
          fill={isSel ? "#2563eb" : existing ? st.fill : usable ? "#ffffff" : "#f1f5f9"}
          stroke={isSel ? "#1e40af" : "#cbd5e1"}
          strokeWidth={isSel ? 3 : 1.5}
          onPointerUp={() => toggle(letter)}
          style={{ cursor: usable ? "pointer" : "not-allowed", touchAction: "manipulation" }}
          opacity={usable ? 1 : 0.4}
        />
      </g>
    );
  };

  const labelPos = { top: [100, 38], bottom: [100, 175], left: [28, 105], right: [172, 105] };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="tooth-detail-sheet">
        <DialogHeader>
          <DialogTitle>
            Tooth {tooth}
            <span className="ml-2 text-sm font-normal text-slate-500">
              {meta?.surfaces_by_tooth?.[tooth]?.includes("I") ? "anterior" : "posterior"}
            </span>
          </DialogTitle>
        </DialogHeader>

        {summariseTooth(entry) ? (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2">
            <span className="text-slate-500">Currently charted: </span>
            <span className="text-slate-900 font-medium">{summariseTooth(entry)}</span>
          </div>
        ) : null}

        <div className="flex justify-center">
          <svg viewBox="-10 -10 220 220" width="240" height="240" style={{ touchAction: "manipulation" }}>
            {region("top", REGION_PATHS.top)}
            {region("bottom", REGION_PATHS.bottom)}
            {region("left", REGION_PATHS.left)}
            {region("right", REGION_PATHS.right)}
            <rect
              x="60" y="60" width="80" height="80"
              fill={selected.includes(sides.centre) ? "#2563eb"
                : entry?.surfaces?.[sides.centre] ? styleFor(entry.surfaces[sides.centre].finding_type).fill : "#ffffff"}
              stroke={selected.includes(sides.centre) ? "#1e40af" : "#cbd5e1"}
              strokeWidth={selected.includes(sides.centre) ? 3 : 1.5}
              onPointerUp={() => toggle(sides.centre)}
              style={{ cursor: "pointer", touchAction: "manipulation" }}
            />
            {Object.entries(labelPos).map(([k, [x, y]]) => (
              <text key={k} x={x} y={y} textAnchor="middle" fontSize="15" fontWeight="700"
                    fill={selected.includes(sides[k]) ? "#ffffff" : "#475569"}
                    style={{ pointerEvents: "none" }}>
                {sides[k]}
              </text>
            ))}
            <text x="100" y="106" textAnchor="middle" fontSize="15" fontWeight="700"
                  fill={selected.includes(sides.centre) ? "#ffffff" : "#475569"}
                  style={{ pointerEvents: "none" }}>
              {sides.centre}
            </text>
          </svg>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Finding</label>
            <select
              value={findingType}
              onChange={(e) => setFindingType(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm mt-1"
              data-testid="tooth-finding-type"
            >
              {Object.entries(meta?.finding_types || {}).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            {isWholeToothFinding ? (
              <div className="text-[11px] text-amber-700 mt-1">
                Applies to the whole tooth — surface selection is ignored.
              </div>
            ) : selected.length === 0 ? (
              <div className="text-[11px] text-slate-500 mt-1">
                Tap one or more surfaces above.
              </div>
            ) : (
              <div className="text-[11px] text-blue-700 mt-1">
                Surfaces: <b>{selected.sort().join(", ")}</b>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm mt-1"
                data-testid="tooth-status"
              >
                {(meta?.statuses || ["existing", "planned", "completed"]).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Material</label>
              <select
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm mt-1"
              >
                <option value="">—</option>
                {(meta?.materials || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={reset} disabled={!selected.length && !note}>
            <Trash2 size={14} className="mr-1" /> Clear
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={apply}
            disabled={!isWholeToothFinding && selected.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="tooth-apply"
          >
            Add to chart
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
